/* eslint-disable no-use-before-define */
import React from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import StructureModel from '@Components/Results/structure/structure-model.service';
import StructureZoomService from '@Services/StructureZoom';
import Structure from '@Components/Results/structure/Structure';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import classnames from 'classnames';
import SubstanceIdentificationChemicalNamesList from '@Components/Results/substances/SubstanceIdentificationChemicalNamesList';

import './SubstanceIdentificationData.scss';

const IDENTIFICATION_DATA_CLASS = 'rx-substance-identification';
const IDENTIFICATION_DATA_ID = 'rx-substance-identification';
const ROW_VARIANTS = {
  PAIRED_START: 'paired-start',
  PAIRED_END: 'paired-end',
  SINGLE_COLUMN: 'single-column',
};

export default function SubstanceIdentificationData({ metadata, database, details, zoom }) {
  const zoomClasses = StructureZoomService.getZoomClasses('substances') || [];
  const zoomClass = zoomClasses[zoom];
  return (
    <>
      {renderIdentificationTable(details, database.isCIDatabase, database.isCommercialSupplierData)}
      <div className="align-structures">{renderMixtureItems(details.mixtureItems, database.id, zoomClass, zoom, metadata)}</div>
    </>
  );
}

function getDescriptionListClassName(type, rowVariant) {
  return classnames(`${IDENTIFICATION_DATA_CLASS}__${type}`, {
    [`${IDENTIFICATION_DATA_CLASS}__${type}--${rowVariant}`]: Boolean(rowVariant),
  });
}

function renderDescriptionListPair(label, value, rowVariant) {
  if (!value) {
    return null;
  }

  return (
    <dl className={`${IDENTIFICATION_DATA_CLASS}__pair`} key={label}>
      <dt className={getDescriptionListClassName('term', rowVariant)}>{label}</dt>
      <InnerHTML tag="dd" className={getDescriptionListClassName('description', rowVariant)} html={value} />
    </dl>
  );
}

function getRowEntries(firstEntry, secondEntry) {
  const hasFirstEntry = Boolean(firstEntry?.value);
  const hasSecondEntry = Boolean(secondEntry?.value);

  if (!hasFirstEntry && !hasSecondEntry) {
    return null;
  }

  if (hasFirstEntry && hasSecondEntry) {
    return [
      { ...firstEntry, rowVariant: ROW_VARIANTS.PAIRED_START },
      { ...secondEntry, rowVariant: ROW_VARIANTS.PAIRED_END },
    ];
  }

  const entry = hasFirstEntry ? firstEntry : secondEntry;
  return [{ ...entry, rowVariant: ROW_VARIANTS.SINGLE_COLUMN }];
}

function renderDescriptionListRow(entries) {
  if (!entries?.length) {
    return null;
  }

  return (
    <div
      className={classnames(`${IDENTIFICATION_DATA_CLASS}__row`, {
        [`${IDENTIFICATION_DATA_CLASS}__row--paired`]: entries.length > 1,
      })}
      key={entries.map(({ label }) => label).join('-')}>
      {entries.map(({ label, value, rowVariant }) => renderDescriptionListPair(label, value, rowVariant))}
    </div>
  );
}

function getSubstanceType(type, isCIDatabase) {
  if (isCIDatabase || isEmpty(type)) {
    return null;
  }

  return isArray(type) ? type.join(', ') : type;
}

function renderIdentificationTable(details, isCIDatabase, isCommercialSupplierData) {
  const { id, type = [], formula, linearFormula, weight, referencesCount, modification, inChiKey, chemicalNames } = details;
  const casRegistryNumbers = (details.casRegistryNumbers || []).join(', ');
  const substanceType = getSubstanceType(type, isCIDatabase);
  const idLabel = isCommercialSupplierData ? 'Commercial Substances ID:' : 'Reaxys ID:';
  const hasChemicalNames = Boolean(chemicalNames?.length);

  // Figma column pairing: two equal columns filled top-to-bottom in field order.
  const rows = [
    getRowEntries(isCIDatabase ? null : { label: idLabel, value: id }, { label: 'CAS Registry Number(s):', value: casRegistryNumbers }),
    getRowEntries({ label: 'Substance type:', value: substanceType }, { label: 'Molecular Formula:', value: formula }),
    getRowEntries({ label: 'Linear Structure Formula:', value: linearFormula }, { label: 'Molecular Weight:', value: weight }),
    getRowEntries(isCIDatabase ? null : { label: 'No of references:', value: referencesCount }, { label: 'InChIKey:', value: inChiKey }),
    getRowEntries({ label: 'Modification:', value: modification }),
  ].filter(Boolean);

  return (
    <div
      className={classnames(
        {
          [IDENTIFICATION_DATA_CLASS]: !isCommercialSupplierData,
        },
        `${IDENTIFICATION_DATA_CLASS}--description-list`,
        `${IDENTIFICATION_DATA_CLASS}--paired-layout`,
        'results-key-value-wrapper'
      )}
      id={IDENTIFICATION_DATA_ID}>
      {hasChemicalNames && (
        <div className={`${IDENTIFICATION_DATA_CLASS}__row ${IDENTIFICATION_DATA_CLASS}__row--chemical-names`}>
          <dl className={`${IDENTIFICATION_DATA_CLASS}__pair`}>
            <SubstanceIdentificationChemicalNamesList chemicalNames={chemicalNames} />
          </dl>
        </div>
      )}
      {rows.map(renderDescriptionListRow)}
    </div>
  );
}

function renderMixtureItems(items, databaseId, zoomClass, zoom, metadata = {}) {
  return map(items, (structure, index) => (
    <div className={classnames('mixture-structure-wrapper', zoomClass)} key={index}>
      <span className="structure-content-label">
        {structure.name} {structure.amount ? `(${structure.amount})` : ''}
      </span>
      <div className="mixture-structure mixture-structure--react">
        <Structure metadata={metadata} structure={formatStructure(structure, databaseId)} zoom={zoom} />
      </div>
    </div>
  ));
}

function formatStructure(structure, databaseId) {
  // eslint-disable-next-line no-param-reassign
  structure.databaseId = databaseId;
  return new StructureModel(structure);
}

SubstanceIdentificationData.propTypes = {
  metadata: MetadataPropTypesShape,
  details: PropTypes.shape({
    id: PropTypes.string,
    chemicalNames: PropTypes.array,
    casRegistryNumbers: PropTypes.array,
    type: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    formula: PropTypes.string,
    linearFormula: PropTypes.string,
    weight: PropTypes.string,
    referencesCount: PropTypes.string,
    modification: PropTypes.string,
    inChiKey: PropTypes.string,
    mixtureItems: PropTypes.array,
  }).isRequired,
  database: PropTypes.object.isRequired,
  zoom: PropTypes.number,
};
